import { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { CheckCircle, Percent, ToggleLeft, ToggleRight, FileArrowDown } from "@phosphor-icons/react";

const RAG = { green: "#00A86B", amber: "#FFB020", red: "#E63946", acknowledged: "#00A86B" };
const FREQS = [
  { id: "monthly", label: "Monthly", hint: "Repayment position / by authorisation" },
  { id: "bi_monthly", label: "Bi-monthly", hint: "Standard (liability > €14,400)" },
  { id: "four_monthly", label: "Four-monthly", hint: "Liability €3,001–€14,400" },
  { id: "six_monthly", label: "Six-monthly", hint: "Liability ≤ €3,000" },
];

export default function VatTab({ onChange }) {
  const [company, setCompany] = useState(null);
  const [vat, setVat] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadVat = useCallback(async () => { const r = await api.get("/compliance/vat"); setVat(r.data); }, []);
  useEffect(() => {
    (async () => {
      const c = await api.get("/compliance/company");
      setCompany(c.data);
      loadVat();
    })();
  }, [loadVat]);

  const persist = async (patch) => {
    const next = { ...company, ...patch };
    setCompany(next);
    setSaving(true);
    try { await api.put("/compliance/company", next); await loadVat(); onChange && onChange(); }
    finally { setSaving(false); }
  };

  const acknowledge = async (deadline) => {
    await api.post("/compliance/vat/acknowledge", { filing_deadline: deadline });
    loadVat(); onChange && onChange();
  };

  if (!company || !vat) return <div className="glass rounded-2xl p-8 text-white/50">Loading…</div>;

  const c = vat.current;
  const rag = c ? (RAG[c.status] || RAG.green) : RAG.green;
  const dr = c?.days_remaining ?? 0;
  const timeText = dr < 0 ? `Overdue by ${Math.abs(dr)} days` : dr === 0 ? "Due today" : `${dr} days left (${c.weeks_remaining} wks)`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <div className="glass rounded-2xl p-8">
        <div className="eyebrow mb-1">VAT registration</div>
        <div className="font-display text-2xl mb-6">VAT return settings</div>

        <button
          onClick={() => persist({ vat_registered: !company.vat_registered })}
          disabled={saving}
          data-testid="vat-registered-toggle"
          className="flex items-center gap-3 w-full rounded-xl bg-black/30 border border-white/5 p-4 mb-5 hover:border-white/15"
        >
          {company.vat_registered ? <ToggleRight size={28} weight="fill" color="#00A86B" /> : <ToggleLeft size={28} color="#8A9A94" />}
          <div className="text-left">
            <div className="text-sm font-semibold">{company.vat_registered ? "VAT registered" : "Not VAT registered"}</div>
            <div className="text-xs text-white/50">Tap to toggle — reminders show only when registered.</div>
          </div>
        </button>

        <label>Filing frequency</label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {FREQS.map((fr) => (
            <button
              key={fr.id}
              onClick={() => persist({ vat_frequency: fr.id })}
              disabled={saving || !company.vat_registered}
              data-testid={`vat-freq-${fr.id}`}
              className={`text-left rounded-xl p-3 border transition-colors ${company.vat_frequency === fr.id ? "border-[#FFB020] bg-[#FFB020]/10" : "border-white/8 bg-black/20 hover:border-white/20"} ${!company.vat_registered ? "opacity-40" : ""}`}
            >
              <div className="text-sm font-semibold">{fr.label}</div>
              <div className="text-[11px] text-white/50 mt-0.5">{fr.hint}</div>
            </button>
          ))}
        </div>
        <div className="text-[11px] text-white/45 mt-4">Deadline = 23rd of the month after the period ends (ROS). Paper filing deadline is the 19th.</div>
      </div>

      <div className="glass rounded-2xl p-8" data-testid="vat-card" style={{ borderColor: c ? `${rag}55` : undefined }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow mb-1">VAT return</div>
            <div className="font-display text-2xl">Next deadline</div>
          </div>
          {c && <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider" style={{ background: `${rag}22`, color: rag, border: `1px solid ${rag}` }} data-testid="vat-status">{c.status === "red" ? "Overdue" : c.status === "amber" ? "Due soon" : "On track"}</span>}
        </div>

        {!company.vat_registered ? (
          <p className="text-sm text-white/55">Enable VAT registration to see your VAT3 filing reminders.</p>
        ) : !c ? (
          <p className="text-sm text-white/55">No VAT periods in range.</p>
        ) : (
          <>
            <div className="rounded-xl p-5" style={{ background: `${rag}12`, border: `1px solid ${rag}40` }}>
              <div className="flex items-center gap-2 text-sm text-white/80"><Percent size={18} weight="fill" style={{ color: rag }} /> Period {c.period_label} · {vat.frequency_label}</div>
              <div className="mono text-2xl mt-3" style={{ color: rag }} data-testid="vat-time">{timeText}</div>
              <div className="text-xs text-white/55 mt-2">Filing deadline (ROS) <span className="mono text-white/80" data-testid="vat-deadline">{c.filing_deadline}</span> · paper <span className="mono text-white/70">{c.paper_deadline}</span></div>
              <button onClick={() => acknowledge(c.filing_deadline)} data-testid="vat-acknowledge" className="btn-gold mt-4 w-full justify-center"><CheckCircle size={16} weight="fill" /> Acknowledge submission (mark filed)</button>
              {c.period_start && c.period_end && (
                <a
                  href={`${API}/exports/vat3.csv?period_start=${c.period_start}&period_end=${c.period_end}`}
                  target="_blank" rel="noreferrer"
                  data-testid="vat-export-csv"
                  className="btn-ghost mt-2 w-full justify-center"
                >
                  <FileArrowDown size={16} weight="fill" /> Download VAT3 CSV for Revenue
                </a>
              )}
              <div className="text-[10px] text-white/40 mt-2">Working/backup document (VAT on purchases from receipts with VAT). File the official VAT3 via ROS.</div>
            </div>
            <div className="eyebrow mt-5 mb-2">Upcoming</div>
            <div className="space-y-2">
              {vat.upcoming.slice(0, 4).map((p) => {
                const pr = RAG[p.status] || RAG.green;
                return (
                  <div key={p.filing_deadline} className="flex items-center justify-between text-sm py-2 border-b border-white/5" data-testid={`vat-upcoming-${p.filing_deadline}`}>
                    <span className="text-white/70">{p.period_label} · <span className="mono text-xs">{p.filing_deadline}</span></span>
                    <span className="text-xs font-semibold" style={{ color: pr.c || pr }}>{p.days_remaining < 0 ? `${Math.abs(p.days_remaining)}d overdue` : `${p.days_remaining}d`}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
